import { useState, useEffect, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { stripeApi } from '../lib/api'
import { useAuthStore } from '../store/authStore'
import type { SubscriptionInfo, SubscriptionTier } from '../types'

// Subscription tier configurations
const SUBSCRIPTION_TIERS = [
  {
    id: 'IRIS' as const,
    name: 'Iris',
    price: 9,
    credits: 15,
    renderQuality: 'STANDARD' as const,
    features: ['15 credits/month', '2K render quality', 'All generation modes', 'Style references'],
  },
  {
    id: 'ARES' as const,
    name: 'Ares',
    price: 19,
    credits: 35,
    renderQuality: 'STANDARD' as const,
    features: ['35 credits/month', '2K render quality', 'All generation modes', 'Style references', 'Priority support'],
  },
  {
    id: 'APOLLO' as const,
    name: 'Apollo',
    price: 39,
    credits: 70,
    renderQuality: 'STANDARD' as const,
    features: ['70 credits/month', '2K render quality', 'All generation modes', 'Style references', 'Priority support'],
    popular: true,
  },
  {
    id: 'ATLAS' as const,
    name: 'Atlas',
    price: 79,
    credits: 75,
    renderQuality: 'PREMIUM' as const,
    features: ['75 credits/month', '4K render quality', 'All generation modes', 'Style references', 'Priority support', 'Early access features'],
  },
]

// Credit pack configurations
const CREDIT_PACKS = [
  { id: 'small', name: 'Small Pack', price: 19, credits: 30 },
  { id: 'medium', name: 'Medium Pack', price: 49, credits: 90 },
  { id: 'large', name: 'Large Pack', price: 79, credits: 160 },
]

export function Subscription() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [_isPolling, setIsPolling] = useState(false)

  // Track initial values for polling comparison
  const initialDataRef = useRef<{ tier: string | null; totalCredits: number } | null>(null)
  const pollingRef = useRef(false)

  // Polling function to wait for webhook to process
  const pollForUpdate = useCallback(async (isCredits: boolean) => {
    if (pollingRef.current) return // Prevent duplicate polling
    pollingRef.current = true
    setIsPolling(true)

    const maxAttempts = 15 // 30 seconds total
    let attempts = 0

    const poll = async () => {
      try {
        const data = await stripeApi.getSubscription()
        const newTotalCredits = data.subscriptionCredits + data.bonusCredits
        const initial = initialDataRef.current

        // Check if data has changed
        if (isCredits) {
          if (initial && newTotalCredits > initial.totalCredits) {
            setSubscription(data)
            setIsPolling(false)
            pollingRef.current = false
            setToast({ type: 'success', message: 'Credits added to your account!' })
            useAuthStore.getState().refreshUser()
            // Clear URL params
            setSearchParams({}, { replace: true })
            return
          }
        } else {
          if (initial && data.tier !== initial.tier && data.tier !== 'FREE') {
            setSubscription(data)
            setIsPolling(false)
            pollingRef.current = false
            setToast({ type: 'success', message: 'Subscription activated successfully!' })
            useAuthStore.getState().refreshUser()
            // Clear URL params
            setSearchParams({}, { replace: true })
            return
          }
        }

        attempts++
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000)
        } else {
          // Timeout - show what we have and consider it success
          setSubscription(data)
          setIsPolling(false)
          pollingRef.current = false
          setToast({ type: 'success', message: isCredits ? 'Credits added!' : 'Subscription updated!' })
          useAuthStore.getState().refreshUser()
          // Clear URL params
          setSearchParams({}, { replace: true })
        }
      } catch (error) {
        setIsPolling(false)
        pollingRef.current = false
        setToast({ type: 'error', message: 'Failed to verify payment. Please refresh the page.' })
      }
    }

    // Start polling after short delay for webhook to process
    setTimeout(poll, 1000)
  }, [setSearchParams])

  // Check for success/cancel URL params and trigger polling
  useEffect(() => {
    const success = searchParams.get('success') === 'true'
    const credits = searchParams.get('credits') === 'true'
    const canceled = searchParams.get('canceled') === 'true'

    if (canceled) {
      setToast({ type: 'error', message: 'Payment was canceled.' })
      setSearchParams({}, { replace: true })
    } else if ((success || credits) && !pollingRef.current) {
      // Store initial data for comparison (will be set after initial fetch)
      // Start polling after initial data is loaded
      if (subscription) {
        initialDataRef.current = {
          tier: subscription.tier,
          totalCredits: subscription.subscriptionCredits + subscription.bonusCredits
        }
        pollForUpdate(credits)
      }
    }
  }, [searchParams, subscription, pollForUpdate, setSearchParams])

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [toast])

  // Fetch subscription status (use cache for instant display, then refresh)
  useEffect(() => {
    async function fetchSubscription() {
      // Try to show cached data immediately
      const cached = stripeApi.getCachedSubscription()
      if (cached) {
        setSubscription(cached)
        setLoading(false)
      }

      // Fetch fresh data in background
      try {
        const data = await stripeApi.getSubscription(true) // Force refresh
        setSubscription(data)
      } catch (error) {
        console.error('Failed to fetch subscription:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchSubscription()
  }, [])

  const handleSubscribe = async (tier: Exclude<SubscriptionTier, 'FREE'>) => {
    setActionLoading(tier)
    try {
      const { url } = await stripeApi.checkoutSubscription(tier)
      window.location.href = url
    } catch (error: any) {
      setToast({ type: 'error', message: error.message || 'Failed to start checkout' })
      setActionLoading(null)
    }
  }

  const handleBuyCredits = async (packType: 'small' | 'medium' | 'large') => {
    setActionLoading(`credits-${packType}`)
    try {
      const { url } = await stripeApi.checkoutCredits(packType)
      window.location.href = url
    } catch (error: any) {
      setToast({ type: 'error', message: error.message || 'Failed to start checkout' })
      setActionLoading(null)
    }
  }

  const handleManageBilling = async () => {
    setActionLoading('portal')
    try {
      const { url } = await stripeApi.openPortal()
      window.location.href = url
    } catch (error: any) {
      setToast({ type: 'error', message: error.message || 'Failed to open billing portal' })
      setActionLoading(null)
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }

  // Calculate subscription credits percentage against 3x cap
  const subscriptionCreditsPercentage = subscription
    ? Math.round((subscription.subscriptionCredits / subscription.maxCredits) * 100)
    : 0

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full border-4 border-primary/20 border-t-primary animate-spin mx-auto mb-4" />
          <p className="text-text-muted">Loading subscription...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-12">
      {/* Toast notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-xl shadow-lg flex items-center gap-3 animate-fade-up ${
            toast.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          <span className="material-symbols-outlined">
            {toast.type === 'success' ? 'check_circle' : 'error'}
          </span>
          {toast.message}
          <button
            onClick={() => setToast(null)}
            className="ml-2 text-current opacity-60 hover:opacity-100"
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>
      )}

      {/* Page header */}
      <div className="animate-fade-up">
        <h1 className="text-3xl md:text-4xl font-semibold text-text-main leading-tight">
          Subscription
        </h1>
        <p className="text-text-muted mt-2">
          Manage your subscription and credits
        </p>
      </div>

      {/* Current plan status */}
      <div
        className="relative overflow-hidden rounded-2xl p-6 md:p-8 animate-fade-up"
        style={{ animationDelay: '50ms' }}
      >
        {/* Glass effect background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-accent/20 to-primary/5 backdrop-blur-sm" />
        <div className="absolute inset-0 border border-white/20 rounded-2xl" />

        <div className="relative z-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/50 rounded-xl shadow-sm">
                  <span className="material-symbols-outlined text-2xl text-primary-dark">
                    workspace_premium
                  </span>
                </div>
                <div>
                  <p className="text-sm text-text-muted font-medium">Current Plan</p>
                  <h2 className="text-2xl font-bold text-primary-dark">
                    {subscription?.tier === 'FREE'
                      ? 'Free'
                      : SUBSCRIPTION_TIERS.find((t) => t.id === subscription?.tier)?.name || subscription?.tier}
                  </h2>
                </div>
              </div>

              {subscription?.tier !== 'FREE' && subscription?.status && (
                <div className="flex items-center gap-4 text-sm">
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                      subscription.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : subscription.status === 'canceled'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        subscription.status === 'active'
                          ? 'bg-green-500'
                          : subscription.status === 'canceled'
                          ? 'bg-red-500'
                          : 'bg-yellow-500'
                      }`}
                    />
                    {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                  </span>
                  {subscription.currentPeriodEnd && (
                    <span className="text-text-muted">
                      Renews {formatDate(subscription.currentPeriodEnd)}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Credits display - Dual pools */}
            <div className="bg-white/60 rounded-xl p-5 min-w-[320px] shadow-sm space-y-4">
              {/* Available Credits Summary */}
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-text-muted">Available Credits</span>
                <span className="text-2xl font-bold text-primary-dark">
                  {subscription?.availableCredits || 0}
                </span>
              </div>

              {/* Subscription Credits Pool */}
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <span className="text-xs font-medium text-text-muted flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm">calendar_month</span>
                    Subscription Pool
                  </span>
                  <span className="text-sm font-semibold text-text-main">
                    {subscription?.subscriptionCredits || 0} / {subscription?.maxCredits || 0}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-primary to-primary-dark h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(subscriptionCreditsPercentage, 100)}%` }}
                  />
                </div>
                <p className="text-[10px] text-text-muted mt-1">
                  Rolls over monthly (max {subscription?.maxCredits || 0} = {subscription?.monthlyCredits || 0} x 3)
                </p>
              </div>

              {/* Credit Packs Pool */}
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <span className="text-xs font-medium text-text-muted flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm">shopping_bag</span>
                    Credit Packs
                  </span>
                  <span className="text-sm font-semibold text-amber-600">
                    {subscription?.bonusCredits || 0}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-amber-400 to-amber-500 h-2 rounded-full"
                    style={{ width: subscription?.bonusCredits ? '100%' : '0%' }}
                  />
                </div>
                <p className="text-[10px] text-text-muted mt-1">
                  Never expires (used after subscription credits)
                </p>
              </div>

              {/* Reserved indicator */}
              {subscription && subscription.reservedCredits > 0 && (
                <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                  <span className="material-symbols-outlined text-sm animate-pulse">hourglass_top</span>
                  {subscription.reservedCredits} credit{subscription.reservedCredits > 1 ? 's' : ''} reserved for in-progress generations
                </div>
              )}
            </div>
          </div>

          {subscription?.hasStripeAccount && (
            <button
              onClick={handleManageBilling}
              disabled={actionLoading === 'portal'}
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-white/70 hover:bg-white text-primary-dark font-medium rounded-xl transition-colors shadow-sm"
            >
              {actionLoading === 'portal' ? (
                <span className="w-4 h-4 border-2 border-primary-dark/30 border-t-primary-dark rounded-full animate-spin" />
              ) : (
                <span className="material-symbols-outlined text-lg">credit_card</span>
              )}
              Manage Billing
            </button>
          )}
        </div>
      </div>

      {/* Subscription tiers */}
      <div className="space-y-4 animate-fade-up" style={{ animationDelay: '100ms' }}>
        <h2 className="text-xl font-semibold text-text-main">Subscription Plans</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {SUBSCRIPTION_TIERS.map((tier, index) => {
            const isCurrentTier = subscription?.tier === tier.id
            return (
              <div
                key={tier.id}
                className={`relative bg-white rounded-2xl border-2 p-6 flex flex-col transition-all hover:shadow-lg animate-fade-up ${
                  isCurrentTier
                    ? 'border-primary shadow-md'
                    : tier.popular
                    ? 'border-primary/50'
                    : 'border-border-color hover:border-primary/30'
                }`}
                style={{ animationDelay: `${150 + index * 50}ms` }}
              >
                {tier.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-white text-xs font-bold rounded-full shadow-sm">
                    Popular
                  </span>
                )}

                <div className="mb-4">
                  <h3 className="text-lg font-bold text-text-main">{tier.name}</h3>
                  <div className="flex items-baseline gap-1 mt-2">
                    <span className="text-3xl font-bold text-primary-dark">${tier.price}</span>
                    <span className="text-text-muted">/month</span>
                  </div>
                </div>

                <ul className="space-y-2.5 flex-1 mb-6">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-text-muted">
                      <span className="material-symbols-outlined text-primary text-base mt-0.5">
                        check_circle
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>

                {isCurrentTier ? (
                  <span className="inline-flex items-center justify-center gap-2 w-full py-3 px-4 bg-primary/10 text-primary-dark font-medium rounded-xl">
                    <span className="material-symbols-outlined text-lg">check</span>
                    Current Plan
                  </span>
                ) : (
                  <button
                    onClick={() => handleSubscribe(tier.id)}
                    disabled={actionLoading === tier.id}
                    className="btn btn-primary w-full justify-center"
                  >
                    {actionLoading === tier.id ? (
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : subscription?.tier === 'FREE' ? (
                      'Get Started'
                    ) : (
                      'Switch Plan'
                    )}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Credit packs */}
      <div className="space-y-4 animate-fade-up" style={{ animationDelay: '200ms' }}>
        <h2 className="text-xl font-semibold text-text-main">Credit Packs</h2>
        <p className="text-text-muted">
          Need more credits? Purchase additional credits that never expire.
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          {CREDIT_PACKS.map((pack, index) => (
            <div
              key={pack.id}
              className="bg-white rounded-2xl border border-border-color p-6 flex flex-col hover:shadow-lg hover:border-primary/30 transition-all animate-fade-up"
              style={{ animationDelay: `${250 + index * 50}ms` }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-text-main">{pack.name}</h3>
                <span className="px-3 py-1 bg-accent/50 text-primary-dark text-sm font-medium rounded-full">
                  {pack.credits} credits
                </span>
              </div>

              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-3xl font-bold text-primary-dark">${pack.price}</span>
                <span className="text-text-muted text-sm">one-time</span>
              </div>

              <p className="text-sm text-text-muted mb-6">
                ${(pack.price / pack.credits).toFixed(2)} per credit
              </p>

              <button
                onClick={() => handleBuyCredits(pack.id as 'small' | 'medium' | 'large')}
                disabled={actionLoading === `credits-${pack.id}`}
                className="btn btn-secondary w-full justify-center mt-auto"
              >
                {actionLoading === `credits-${pack.id}` ? (
                  <span className="w-5 h-5 border-2 border-primary-dark/30 border-t-primary-dark rounded-full animate-spin" />
                ) : (
                  <>
                    <span className="material-symbols-outlined text-lg">shopping_cart</span>
                    Buy Now
                  </>
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ or help section */}
      <div
        className="bg-surface-soft rounded-2xl p-6 border border-border-color animate-fade-up"
        style={{ animationDelay: '300ms' }}
      >
        <h3 className="font-semibold text-text-main mb-3">Questions about billing?</h3>
        <p className="text-sm text-text-muted">
          You can manage your subscription, update payment methods, view invoices, and cancel
          anytime through the Stripe Customer Portal by clicking "Manage Billing" above.
        </p>
      </div>
    </div>
  )
}
